import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import connectorService from '../../services/connector.service';
import type { ConnectorRoute, CreateRouteData, UpdateRouteData } from '../../services/connector.service';
import DataTable, { type Column } from '../common/DataTable';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';

interface RoutesTabProps {
  connectorId: string;
}

const RoutesTab = ({ connectorId }: RoutesTabProps) => {
  const [routes, setRoutes] = useState<ConnectorRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRoute, setEditRoute] = useState<ConnectorRoute | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; route?: ConnectorRoute }>({ show: false });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState<CreateRouteData>({
    destination: '',
    gateway: '',
    interfaceName: '',
    metric: 100,
    description: '',
    enabled: true,
  });

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const data = await connectorService.listRoutes(connectorId);
      setRoutes(data);
    } catch {
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoutes(); }, [connectorId]);

  const openAdd = () => {
    setEditRoute(null);
    setFormData({ destination: '', gateway: '', interfaceName: '', metric: 100, description: '', enabled: true });
    setModalOpen(true);
  };

  const openEdit = (route: ConnectorRoute) => {
    setEditRoute(route);
    setFormData({
      destination: route.destination,
      gateway: route.gateway || '',
      interfaceName: route.interface_name || '',
      metric: route.metric,
      description: route.description || '',
      enabled: route.enabled === 1,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editRoute) {
        await connectorService.updateRoute(connectorId, editRoute.id, formData as UpdateRouteData);
        toast.success('Route updated');
      } else {
        await connectorService.addRoute(connectorId, formData);
        toast.success('Route added');
      }
      setModalOpen(false);
      fetchRoutes();
    } catch {
      toast.error(editRoute ? 'Failed to update route' : 'Failed to add route');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.route) return;
    try {
      setDeleting(true);
      await connectorService.deleteRoute(connectorId, deleteConfirm.route.id);
      toast.success('Route deleted');
      setDeleteConfirm({ show: false });
      fetchRoutes();
    } catch {
      toast.error('Failed to delete route');
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<ConnectorRoute>[] = [
    {
      key: 'destination',
      label: 'Destination',
      render: (route) => <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">{route.destination}</code>,
    },
    {
      key: 'gateway',
      label: 'Gateway',
      render: (route) => route.gateway || <span className="text-gray-400">—</span>,
    },
    {
      key: 'metric',
      label: 'Metric',
      render: (route) => route.metric,
    },
    {
      key: 'enabled',
      label: 'Status',
      render: (route) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${route.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
          {route.enabled ? 'Enabled' : 'Disabled'}
        </span>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      render: (route) => route.description || <span className="text-gray-400">—</span>,
    },
    {
      key: 'actions',
      label: '',
      render: (route) => (
        <div className="flex items-center space-x-2">
          <button onClick={() => openEdit(route)} className="text-gray-500 hover:text-primary-600"><Pencil className="w-4 h-4" /></button>
          <button onClick={() => setDeleteConfirm({ show: true, route })} className="text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      ),
    },
  ];

  const modalFooter = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
      <button onClick={handleSave} disabled={saving || !formData.destination} className="btn-primary disabled:opacity-50">
        {saving ? 'Saving...' : editRoute ? 'Update Route' : 'Add Route'}
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Static Routes</h3>
        <button onClick={openAdd} className="btn-primary inline-flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Add Route</span>
        </button>
      </div>

      <div className="card">
        <DataTable columns={columns} data={routes} searchPlaceholder="Search routes..." loading={loading} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editRoute ? 'Edit Route' : 'Add Route'} size="md" footer={modalFooter}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination CIDR <span className="text-red-500">*</span></label>
            <input type="text" value={formData.destination} onChange={(e) => setFormData(d => ({ ...d, destination: e.target.value }))} placeholder="e.g., 10.0.0.0/8" className="input-base w-full" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gateway</label>
              <input type="text" value={formData.gateway || ''} onChange={(e) => setFormData(d => ({ ...d, gateway: e.target.value }))} placeholder="Next-hop IP" className="input-base w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Metric</label>
              <input type="number" value={formData.metric} onChange={(e) => setFormData(d => ({ ...d, metric: parseInt(e.target.value) || 100 }))} className="input-base w-full" min={0} max={9999} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={formData.description || ''} onChange={(e) => setFormData(d => ({ ...d, description: e.target.value }))} placeholder="Optional description" className="input-base w-full" />
          </div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input type="checkbox" checked={formData.enabled} onChange={(e) => setFormData(d => ({ ...d, enabled: e.target.checked }))} className="w-4 h-4 text-primary-600 border-gray-300 rounded" />
            <span className="text-sm text-gray-700">Enabled</span>
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="Delete Route"
        message={`Delete route to ${deleteConfirm.route?.destination}?`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ show: false })}
        isLoading={deleting}
      />
    </div>
  );
};

export default RoutesTab;
